import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Users, Gift, Crown, TrendingUp, RefreshCw, Lock } from "lucide-react";
import { PageShell } from "../components/PageShell";
import { ClayButton } from "../components/ClayButton";
import { getToken } from "../lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div
      className="bg-white rounded-3xl border-4 border-white shadow-md p-5 flex flex-col gap-2"
      style={{ borderColor: color + "22" }}
      data-testid={`stat-card-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      <div className="flex items-center gap-3">
        <div
          className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
          style={{ background: color }}
        >
          <Icon className="w-6 h-6 text-white" strokeWidth={2.5} />
        </div>
        <p className="text-sm font-bold uppercase tracking-wide text-[#5C5C60]">
          {label}
        </p>
      </div>
      <p className="font-heading text-4xl font-bold text-[#1C1C1E]">{value}</p>
      {sub && <p className="text-xs text-[#5C5C60]">{sub}</p>}
    </div>
  );
}

export default function AdminStats() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setData(data);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) {
        setError("forbidden");
      } else if (status === 401) {
        setError("auth");
      } else {
        setError("network");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const monthName = new Date().toLocaleDateString("es-MX", {
    month: "long",
    year: "numeric",
  });

  return (
    <PageShell>
      <div className="mt-4 flex items-center justify-between">
        <ClayButton
          variant="ghost"
          onClick={() => navigate("/dashboard")}
          data-testid="admin-back-btn"
        >
          <ArrowLeft className="w-5 h-5" strokeWidth={3} />
          Volver
        </ClayButton>
        {!error && (
          <ClayButton
            variant="secondary"
            onClick={load}
            disabled={loading}
            data-testid="admin-refresh-btn"
          >
            <RefreshCw
              className={`w-5 h-5 ${loading ? "animate-spin" : ""}`}
              strokeWidth={3}
            />
            Actualizar
          </ClayButton>
        )}
      </div>

      <div className="max-w-5xl mx-auto mt-4">
        <h1
          className="font-heading text-3xl sm:text-4xl font-bold text-[#1C1C1E] mb-2"
          data-testid="admin-title"
        >
          Panel Admin
        </h1>
        <p className="text-sm text-[#5C5C60] mb-6">
          Métricas de MateConMar · solo para el propietario de la cuenta.
        </p>

        {loading && (
          <p className="text-[#5C5C60]" data-testid="admin-loading">
            Cargando métricas…
          </p>
        )}

        {error === "forbidden" && (
          <div
            className="bg-[#FFEAEA] border-2 border-[#FF6B6B] text-[#803535] rounded-3xl p-5 flex items-start gap-3"
            data-testid="admin-forbidden"
          >
            <Lock className="w-6 h-6 shrink-0" strokeWidth={2.5} />
            <div>
              <p className="font-bold mb-1">Acceso restringido</p>
              <p className="text-sm">
                Esta pantalla solo la puede ver la cuenta administradora. Inicia
                sesión con la cuenta configurada como admin.
              </p>
            </div>
          </div>
        )}

        {error === "auth" && (
          <div className="bg-[#FFEAEA] border-2 border-[#FF6B6B] text-[#803535] rounded-3xl p-5">
            <p className="font-bold">Sesión expirada</p>
            <p className="text-sm">Inicia sesión de nuevo para ver el panel.</p>
          </div>
        )}

        {error === "network" && (
          <div className="bg-[#FFEAEA] border-2 border-[#FF6B6B] text-[#803535] rounded-3xl p-5">
            <p className="font-bold">No se pudo cargar</p>
            <p className="text-sm">
              Hubo un problema conectando con el servidor. Intenta de nuevo.
            </p>
          </div>
        )}

        {!loading && !error && data && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <StatCard
                icon={Users}
                label="Alumnos totales"
                value={data.total_users}
                sub="Registrados históricos"
                color="#5C7CFA"
              />
              <StatCard
                icon={Gift}
                label="En prueba 7 días"
                value={data.trial_active}
                sub="Aún dentro de su prueba"
                color="#34C759"
              />
              <StatCard
                icon={Crown}
                label="Premium activo"
                value={data.premium_active}
                sub="Con suscripción vigente"
                color="#FF9500"
              />
              <StatCard
                icon={TrendingUp}
                label={`Ingresos (${monthName})`}
                value={`$${data.revenue_this_month.total_mxn.toLocaleString(
                  "es-MX"
                )}`}
                sub={`${data.revenue_this_month.monthly_count} Mensual · ${data.revenue_this_month.yearly_count} Anual`}
                color="#B983FF"
              />
            </div>

            {/* Revenue breakdown */}
            <div
              className="bg-white rounded-3xl border-4 border-white shadow-md p-5 sm:p-6 mb-6"
              data-testid="admin-revenue-card"
            >
              <h2 className="font-heading text-xl font-bold text-[#1C1C1E] mb-4">
                Ingresos del mes · desglose
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-[#FFF6CC] border-2 border-[#FFD700] rounded-2xl p-4">
                  <p className="text-xs font-bold uppercase text-[#7A5C00] tracking-wide">
                    Plan Mensual
                  </p>
                  <p className="font-heading text-3xl font-bold text-[#1C1C1E] mt-1">
                    ${data.revenue_this_month.monthly_mxn.toLocaleString("es-MX")}{" "}
                    <span className="text-base text-[#5C5C60]">MXN</span>
                  </p>
                  <p className="text-xs text-[#7A5C00] mt-1">
                    {data.revenue_this_month.monthly_count} pago
                    {data.revenue_this_month.monthly_count === 1 ? "" : "s"}
                  </p>
                </div>
                <div className="bg-[#FFEEDC] border-2 border-[#FF9500] rounded-2xl p-4">
                  <p className="text-xs font-bold uppercase text-[#B05A00] tracking-wide">
                    Plan Anual
                  </p>
                  <p className="font-heading text-3xl font-bold text-[#1C1C1E] mt-1">
                    ${data.revenue_this_month.yearly_mxn.toLocaleString("es-MX")}{" "}
                    <span className="text-base text-[#5C5C60]">MXN</span>
                  </p>
                  <p className="text-xs text-[#B05A00] mt-1">
                    {data.revenue_this_month.yearly_count} pago
                    {data.revenue_this_month.yearly_count === 1 ? "" : "s"}
                  </p>
                </div>
              </div>
            </div>

            {/* Recent registrations */}
            <div
              className="bg-white rounded-3xl border-4 border-white shadow-md p-5 sm:p-6"
              data-testid="admin-recent-card"
            >
              <h2 className="font-heading text-xl font-bold text-[#1C1C1E] mb-4">
                Últimos 10 registros
              </h2>
              {data.recent_registrations.length === 0 && (
                <p className="text-sm text-[#5C5C60]">Aún no hay registros.</p>
              )}
              {data.recent_registrations.length > 0 && (
                <ul className="divide-y divide-gray-100" data-testid="admin-recent-list">
                  {data.recent_registrations.map((u, i) => (
                    <li
                      key={u.username + i}
                      className="py-3 flex items-center justify-between gap-3"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-bold text-[#1C1C1E] truncate">
                          {(u.nombre + " " + u.apellido).trim() || u.username}
                        </p>
                        <p className="text-xs text-[#5C5C60] truncate">
                          @{u.username} · {u.email || "sin correo"}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <span className="text-xs text-[#5C5C60]">
                          {fmtDate(u.created_at)}
                        </span>
                        {u.is_premium && (
                          <span className="text-[10px] font-bold bg-[#FFF6CC] text-[#7A5C00] px-2 py-0.5 rounded-full">
                            PREMIUM
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <p className="text-xs text-[#5C5C60] text-center mt-6">
              Generado: {fmtDate(data.generated_at)}
            </p>
          </>
        )}
      </div>
    </PageShell>
  );
}
